// @flow
import objectId from 'bson-objectid';

/**
 * @module modules/model
 */

export opaque type ModelId = string;

export type Model = $ReadOnly<{|
  id: ModelId,
|}>;

/**
 * Take a `camelCaseName` and return a `snake_case_name`.
 *
 * @memberof modules/model
 */
export const snakeize = (str: string): string =>
  [
    str.substr(0, 1).toLowerCase(),
    str.substr(1).replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`),
  ].join('');

/**
 * Get a field off on an object regardless of whether it's camel- or
 * snake-cased.
 *
 * @memberof modules/model
 */
export const getField = (model?: any, field: string): any => {
  if (!model) return model;
  if (model.get) return model.get(field);
  return model[field] || model[snakeize(field)];
};

/**
 * Returns a new BSON::ObjectId.
 *
 * This is used to uniquely identify models when they are just created and do
 * not yet have their own IDs.
 *
 * @todo Side effect. This generates a new ID from the ether.
 * @memberof modules/model
 * @function
 * @returns string
 */
export const makeId = (): ModelId => objectId().str;

// read-impure, side-effect
export const withNewId = <T: $ReadOnly<{||}>>(
  model: T,
): {| ...T, +id: ModelId |} => ({
  ...model,
  id: makeId(),
});

export const asKey = (id: ModelId): string => id;

// eslint-disable-next-line camelcase
export const dontUse__asModelId = (str: string): ModelId => str;
